import type { AddressSnapshot, Order, UserAddress } from '../types';
import type { UserAddressInput } from '../types/api';

export const MAX_USER_ADDRESSES = 20;

export function emptyAddressInput(): UserAddressInput {
  return {
    label: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    district: '',
    city: '',
    zip: '',
    country: 'TR',
    isDefaultShipping: false,
    isDefaultBilling: false,
  };
}

export function addressSnapshotFromUserAddress(address: UserAddress): AddressSnapshot {
  return {
    address: address.address,
    city: address.city,
    district: address.district,
    zip: address.zip,
  };
}

export function formatAddressLine(address: AddressSnapshot): string {
  return `${address.address}, ${address.district}, ${address.city} ${address.zip}`;
}

export function resolveOrderBillingAddress(
  order: Pick<Order, 'shippingAddress' | 'billingAddress' | 'billingSameAsShipping'>,
): AddressSnapshot {
  if (order.billingSameAsShipping === false && order.billingAddress) {
    return order.billingAddress;
  }

  return order.shippingAddress;
}

export function isOrderBillingSameAsShipping(
  order: Pick<Order, 'shippingAddress' | 'billingAddress' | 'billingSameAsShipping'>,
): boolean {
  return order.billingSameAsShipping !== false || !order.billingAddress;
}

export function formatAddressRecipient(address: Pick<UserAddress, 'firstName' | 'lastName' | 'phone'>): string {
  return `${address.firstName} ${address.lastName} · ${address.phone}`;
}

export function userAddressInputFromUserAddress(address: UserAddress): UserAddressInput {
  return {
    label: address.label,
    firstName: address.firstName,
    lastName: address.lastName,
    phone: address.phone,
    address: address.address,
    district: address.district,
    city: address.city,
    zip: address.zip,
    country: address.country,
    isDefaultShipping: address.isDefaultShipping,
    isDefaultBilling: address.isDefaultBilling,
  };
}

export function sortAddresses(addresses: UserAddress[]): UserAddress[] {
  return [...addresses].sort((a, b) => {
    const aScore = (a.isDefaultShipping ? 2 : 0) + (a.isDefaultBilling ? 1 : 0);
    const bScore = (b.isDefaultShipping ? 2 : 0) + (b.isDefaultBilling ? 1 : 0);
    if (aScore !== bScore) return bScore - aScore;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

export function getDefaultShippingAddress(addresses: UserAddress[]): UserAddress | undefined {
  return addresses.find((address) => address.isDefaultShipping);
}

export function getDefaultBillingAddress(addresses: UserAddress[]): UserAddress | undefined {
  return addresses.find((address) => address.isDefaultBilling);
}
